import { useEffect, useRef, useCallback } from 'react';

type ImpressionCallback = (key: string) => void;

export function useImpressionTracking(
  onImpression: ImpressionCallback,
  threshold = 0.5,
): (key: string) => React.RefCallback<HTMLElement> {
  const callbackRef = useRef(onImpression);
  callbackRef.current = onImpression;
  const firedRef = useRef(new Set<string>());
  const pendingRef = useRef(new Map<string, HTMLElement>());
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const key = (entry.target as HTMLElement).dataset.impressionKey;
          if (!key || firedRef.current.has(key)) continue;
          firedRef.current.add(key);
          callbackRef.current(key);
          observer.unobserve(entry.target);
        }
      },
      { threshold },
    );
    observerRef.current = observer;

    pendingRef.current.forEach(node => {
      observer.observe(node);
    });
    pendingRef.current.clear();

    return () => observer.disconnect();
  }, [threshold]);

  return useCallback(
    (key: string) => (node: HTMLElement | null) => {
      if (!node) return;
      node.dataset.impressionKey = key;
      if (observerRef.current) {
        observerRef.current.observe(node);
      } else {
        pendingRef.current.set(key, node);
      }
    },
    [],
  );
}
